import { useState, useEffect } from "react";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { EXPENSE_LABELS } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { Loader2, Plus, Trash2, TrendingUp, TrendingDown, Users, PiggyBank, Fuel, Truck, Wrench, Hammer, Laptop, Megaphone, Shield, Landmark, CircleEllipsis } from "lucide-react";

const CAT_ICONS = { fuel: Fuel, vehicle: Truck, maintenance: Wrench, equipment: Hammer, software: Laptop, marketing: Megaphone, insurance: Shield, tax: Landmark, other: CircleEllipsis };

export default function Finance() {
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ category: "fuel", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });

  const load = () => {
    api.get("/finance/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/finance/expenses").then((r) => setExpenses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addExpense = async () => {
    setSaving(true);
    try {
      await api.post("/finance/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense logged");
      setAddOpen(false);
      setForm({ category: "fuel", amount: "", description: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const deleteExpense = async (id) => {
    await api.delete(`/finance/expenses/${id}`);
    toast.success("Expense removed");
    load();
  };

  if (!summary)
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const net = summary.net_profit;
  const tooltipStyle = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 };
  const maxCat = Math.max(1, ...summary.by_category.map((c) => c.amount));

  return (
    <div data-testid="ops-finance-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Money In · Money Out</p>
          <h1 className="page-title mt-1.5">Finance</h1>
        </div>
        <Button data-testid="finance-add-expense-button" onClick={() => setAddOpen(true)}
          className="rounded-full h-11 btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold px-6">
          <Plus className="w-4 h-4 mr-1.5" /> Log Expense
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-xl border bg-card p-6 card-lift" data-testid="finance-kpi-revenue">
          <TrendingUp className="w-4 h-4 text-emerald-500" />
          <p className="text-3xl font-heading font-bold mt-2">{fmtMoney(summary.revenue)}</p>
          <p className="text-xs text-muted-foreground mt-1">Revenue (paid invoices)</p>
        </div>
        <div className="rounded-xl border bg-card p-6 card-lift" data-testid="finance-kpi-expenses">
          <TrendingDown className="w-4 h-4 text-rose-500" />
          <p className="text-3xl font-heading font-bold mt-2">{fmtMoney(summary.expenses_total)}</p>
          <p className="text-xs text-muted-foreground mt-1">Company expenses</p>
        </div>
        <div className="rounded-xl border bg-card p-6 card-lift" data-testid="finance-kpi-payroll">
          <Users className="w-4 h-4 text-blue-500" />
          <p className="text-3xl font-heading font-bold mt-2">{fmtMoney(summary.payroll_total)}</p>
          <p className="text-xs text-muted-foreground mt-1">Crew payouts</p>
        </div>
        <div className={`rounded-xl border p-6 card-lift ${net >= 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid="finance-kpi-net">
          <PiggyBank className={`w-4 h-4 ${net >= 0 ? "text-emerald-500" : "text-rose-500"}`} />
          <p className={`text-3xl font-heading font-bold mt-2 ${net >= 0 ? "text-emerald-500" : "text-rose-500"}`}>{fmtMoney(net)}</p>
          <p className="text-xs text-muted-foreground mt-1">Net profit — yours</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="rounded-xl border bg-card p-6" data-testid="finance-monthly-chart">
          <h2 className="font-heading font-semibold mb-5">Revenue vs Spend — last 6 months</h2>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={summary.monthly} barGap={4}>
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={54} tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="var(--cc-accent)" radius={[6, 6, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses + payroll" fill="#F43F5E" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border bg-card p-6" data-testid="finance-category-breakdown">
          <h2 className="font-heading font-semibold mb-5">Spend by Category</h2>
          {summary.by_category.length === 0 && (
            <p className="text-sm text-muted-foreground py-8 text-center">No expenses logged yet. Track gas, vehicles, maintenance, software, taxes — everything the company spends.</p>
          )}
          <div className="space-y-4">
            {summary.by_category.sort((a, b) => b.amount - a.amount).map((c) => {
              const Icon = CAT_ICONS[c.category] || CircleEllipsis;
              return (
                <div key={c.category} data-testid={`finance-cat-${c.category}`}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="flex items-center gap-2"><Icon className="w-4 h-4 text-muted-foreground" /> {EXPENSE_LABELS[c.category] || c.category}</span>
                    <span className="font-mono-cc font-semibold">{fmtMoney(c.amount)}</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-[var(--cc-accent)]" style={{ width: `${Math.max(4, (c.amount / maxCat) * 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="rounded-xl border bg-card" data-testid="finance-expense-list">
        <div className="px-6 py-4 border-b"><h2 className="font-heading font-semibold">Expense Log</h2></div>
        <div className="divide-y max-h-96 overflow-y-auto">
          {expenses.map((e) => {
            const Icon = CAT_ICONS[e.category] || CircleEllipsis;
            return (
              <div key={e.id} className="px-6 py-3.5 flex items-center justify-between gap-4" data-testid={`expense-row-${e.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-muted-foreground" /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{EXPENSE_LABELS[e.category]}{e.description ? ` — ${e.description}` : ""}</p>
                    <p className="text-[11px] text-muted-foreground">{fmtDate(e.date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-mono-cc font-semibold text-rose-500">− {fmtMoney(e.amount)}</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-500" data-testid={`expense-delete-${e.id}`}
                    onClick={() => deleteExpense(e.id)} aria-label="Delete expense">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
          {expenses.length === 0 && <p className="px-6 py-10 text-sm text-muted-foreground text-center">Nothing logged yet. Hit "Log Expense" to start tracking company spend.</p>}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-expense-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Log an expense</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(summary.categories || Object.keys(EXPENSE_LABELS)).map((c) => (
                      <SelectItem key={c} value={c}>{EXPENSE_LABELS[c] || c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Amount ($)</Label>
                <Input data-testid="expense-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="120.00" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input data-testid="expense-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea data-testid="expense-description-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Fuel — Box Truck 1, Vegas run" />
            </div>
            <Button data-testid="expense-save-button" onClick={addExpense} disabled={saving || !form.amount || Number(form.amount) <= 0}
              className="w-full rounded-full h-11 btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Expense"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
